import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useScript } from '@hooks/useScript';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.min.js';
import 'leaflet/dist/leaflet.css';
import {
    BasketDetailPage,
    BasketsPage,
    CampaignsPage,
    ContactsPage,
    HomePage,
    HowToUsePointsPage,
    MerchantDetailPage,
    MerchantsPage,
    PointsServiceDetailPage,
    PointsServicesPage,
    PolicyPage,
    ProductDetailPage,
    ProductsPage,
    ProductsSearchResultsPage,
    ProductsSubcategoryPage,
    ServicesPage,
    ServicesSearchResultsPage,
} from '@pages/marketplace';
import {
    CreateAccountPage,
    LoginPage,
    PasswordRecoveryPage,
    SetNewPasswordPage,
    TwoFactorAuthActivatePage,
    TwoFactorAuthVerifyPage,
} from '@pages/authentication';
import {
    ComplaintPage,
    DashboardPage,
    ManageAddressesCreatePage,
    ManageAddressesEditPage,
    ManageAddressesPage,
    ManageOrderDetailPage,
    ManageOrdersPage,
    ManageProductReservationDetailPage,
    ManageProductReservationsPage,
    ManageProductsCreatePage,
    ManageProductsEditPage,
    ManageProductsPage,
    ManageSchedulePage,
    ManageServicesCreatePage,
    ManageServicesEditPage,
    ManageServicesPage,
    ManageWorkersCreatePage,
    ManageWorkersEditPage,
    ManageWorkersPage,
    MyAccountPointsHistoryPage,
    MyAdherenceDetailPage,
    MyAdherencesPage,
    MyCompaniesPage,
    MyCompanyCreatePage,
    MyCompanyPage,
    MyCouponsCreatePage,
    MyCouponsEditPage,
    MyCouponsPage,
    MyOrderDetailPage,
    MyOrdersPage,
    MyProductReservationDetailPage,
    MyProductReservationsPage,
    MyProfilePage,
} from '@pages/myAccount';
import {
    BillingPage,
    CardPaymentRedirectPage,
    ConfirmOrderPage,
    DeliveryAddressPage,
    FeedbackPage,
    PaymentPage,
    ShoppingCartPage,
} from '@pages/shoppingCart';
import {
    ServicesBookingConfirmPage,
    ServicesBookingEmployeesPage,
    ServicesBookingFeedbackPage,
    ServicesBookingPaymentPage,
    ServicesBookingSchedulingPage,
    ServicesCartPage,
} from '@pages/servicesCart';
import reportWebVitals from './reportWebVitals';
import './i18n/config';
import '@styles/GlobalStyles.css';
import { SiteSetup } from '@pages/SiteSetup';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useGetAllLanguages } from '@services/useGetAllLanguages';
import { DataProvider, NotificationsProvider, ServicesCartProvider, ThemesProvider } from '@context';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { MaintenancePage, PageNotFound } from '@pages/errorPages';

export default function App() {
    const { importScript } = useScript();

    const { allLanguages, allSettings } = useData();
    const { request: requestAllLanguages, loading: loadingAllLanguages } = useGetAllLanguages();

    const location = useLocation();
    const currentPath = location.pathname;
    const urlSegments = currentPath.split('/');
    const [currentFaviconSrc, setCurrentFaviconSrc] = useState<string>('');

    // import font awesome
    useEffect(() => {
        importScript('https://kit.fontawesome.com/18bb4f4911.js');
    }, []);

    // Fetch languages
    useEffect(() => {
        !allLanguages && !loadingAllLanguages && requestAllLanguages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allLanguages]);

    useEffect(() => {
        if (allSettings?.data) {
            const data = allSettings.data;
            const faviconItem = data.layouts?.find((item) => item.name === 'favicon');

            let finalSrc = null;
            if (faviconItem?.value) {
                finalSrc = faviconItem.value;
            }

            if (finalSrc) {
                setCurrentFaviconSrc(finalSrc);

                const existingLinks = document.querySelectorAll("link[rel*='icon']");
                existingLinks.forEach((link) => link.remove());

                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = finalSrc;
                link.type = 'image/x-icon';

                document.head.appendChild(link);
            }
        }

        return () => {
            if (currentFaviconSrc) {
                const dynamicLink = document.querySelector(`link[href="${currentFaviconSrc}"]`);
                if (dynamicLink) {
                    dynamicLink.remove();
                }
            }
        };
    }, [allSettings, currentFaviconSrc]);

    const supportedLanguages = allLanguages?.data
        ?.filter((item) => item.language_code !== undefined)
        .map((item) => item.language_code);

    // waiting for languages service return
    if (supportedLanguages?.length === 0) {
        return <></>;
    }

    const availableRoutes = () => (
        <>
            {/* authentication routes */}
            <Route path={SITE_URLS.createAccount} element={<CreateAccountPage />} />
            <Route path={SITE_URLS.login} element={<LoginPage />} />
            <Route path={SITE_URLS.recoverPassword} element={<PasswordRecoveryPage />} />
            <Route path={SITE_URLS.setNewPassword} element={<SetNewPasswordPage />} />
            <Route path={SITE_URLS.twoFactorAuthActivate} element={<TwoFactorAuthActivatePage />} />
            <Route path={SITE_URLS.twoFactorAuthVerify} element={<TwoFactorAuthVerifyPage />} />
            {/* marketplace routes */}
            <Route path={SITE_URLS.home} element={<HomePage />} />
            <Route path={SITE_URLS.products} element={<ProductsPage />} />
            <Route path={SITE_URLS.productsSearch} element={<ProductsSearchResultsPage />} />
            <Route path={SITE_URLS.productsSubcategory} element={<ProductsSubcategoryPage />} />
            <Route path={SITE_URLS.productDetail} element={<ProductDetailPage />} />
            <Route path={SITE_URLS.services} element={<ServicesPage />} />
            <Route path={SITE_URLS.servicesSearch} element={<ServicesSearchResultsPage />} />
            <Route path={SITE_URLS.merchants} element={<MerchantsPage />} />
            <Route path={SITE_URLS.merchantDetail} element={<MerchantDetailPage />} />
            <Route path={SITE_URLS.promoBaskets} element={<BasketsPage />} />
            <Route path={SITE_URLS.promoBasketDetail} element={<BasketDetailPage />} />
            <Route path={SITE_URLS.campaigns} element={<CampaignsPage />} />
            <Route path={SITE_URLS.contacts} element={<ContactsPage />} />
            <Route path={SITE_URLS.howToUsePoints} element={<HowToUsePointsPage />} />
            <Route path={SITE_URLS.pointsServices} element={<PointsServicesPage />} />
            <Route path={SITE_URLS.pointsServiceDetail} element={<PointsServiceDetailPage />} />
            <Route path={SITE_URLS.policy} element={<PolicyPage />} />
            {/* shopping cart pages */}
            <Route path={SITE_URLS.shoppingCart} element={<ShoppingCartPage />} />
            <Route path={SITE_URLS.orderBilling} element={<BillingPage />} />
            <Route path={SITE_URLS.orderDeliveryAddress} element={<DeliveryAddressPage />} />
            <Route path={SITE_URLS.orderPayment} element={<PaymentPage />} />
            <Route path={SITE_URLS.orderConfirm} element={<ConfirmOrderPage />} />
            <Route path={SITE_URLS.orderFeedback} element={<FeedbackPage />} />
            <Route path={SITE_URLS.cardPaymentRedirect} element={<CardPaymentRedirectPage />} />
            {/* services cart pages */}
            <Route path={SITE_URLS.servicesCart} element={<ServicesCartPage />} />
            <Route path={SITE_URLS.servicesBookingEmployees} element={<ServicesBookingEmployeesPage />} />
            <Route path={SITE_URLS.servicesBookingScheduling} element={<ServicesBookingSchedulingPage />} />
            <Route path={SITE_URLS.servicesBookingPayment} element={<ServicesBookingPaymentPage />} />
            <Route path={SITE_URLS.servicesBookingConfirm} element={<ServicesBookingConfirmPage />} />
            <Route path={SITE_URLS.servicesBookingFeedback} element={<ServicesBookingFeedbackPage />} />
            {/* my account pages */}
            <Route path={SITE_URLS.myDashboard} element={<DashboardPage />} />
            <Route path={SITE_URLS.complaint} element={<ComplaintPage />} />
            <Route path={SITE_URLS.myProfile} element={<MyProfilePage />} />
            <Route path={SITE_URLS.myOrders} element={<MyOrdersPage />} />
            <Route path={SITE_URLS.myOrderDetail} element={<MyOrderDetailPage />} />
            <Route path={SITE_URLS.myProductReservations} element={<MyProductReservationsPage />} />
            <Route path={SITE_URLS.myProductReservationDetail} element={<MyProductReservationDetailPage />} />
            <Route path={SITE_URLS.myCompanies} element={<MyCompaniesPage />} />
            <Route path={SITE_URLS.myCompaniesCreate} element={<MyCompanyCreatePage />} />
            <Route path={SITE_URLS.myCompaniesEdit} element={<MyCompanyPage />} />
            <Route path={SITE_URLS.myAdherences} element={<MyAdherencesPage />} />
            <Route path={SITE_URLS.myAdherenceDetail} element={<MyAdherenceDetailPage />} />
            <Route path={SITE_URLS.myCoupons} element={<MyCouponsPage />} />
            <Route path={SITE_URLS.myCouponsCreate} element={<MyCouponsCreatePage />} />
            <Route path={SITE_URLS.myCouponsEdit} element={<MyCouponsEditPage />} />
            <Route path={SITE_URLS.myServices} element={<ManageServicesPage />} />
            <Route path={SITE_URLS.myServicesEdit} element={<ManageServicesEditPage />} />
            <Route path={SITE_URLS.myServicesCreate} element={<ManageServicesCreatePage />} />
            <Route path={SITE_URLS.myWorkers} element={<ManageWorkersPage />} />
            <Route path={SITE_URLS.myWorkersEdit} element={<ManageWorkersEditPage />} />
            <Route path={SITE_URLS.myWorkersCreate} element={<ManageWorkersCreatePage />} />
            <Route path={SITE_URLS.mySchedule} element={<ManageSchedulePage />} />
            <Route path={SITE_URLS.myProducts} element={<ManageProductsPage />} />
            <Route path={SITE_URLS.myProductsCreate} element={<ManageProductsCreatePage />} />
            <Route path={SITE_URLS.myProductsEdit} element={<ManageProductsEditPage />} />
            <Route path={SITE_URLS.manageOrders} element={<ManageOrdersPage />} />
            <Route path={SITE_URLS.manageOrderDetail} element={<ManageOrderDetailPage />} />
            <Route path={SITE_URLS.manageProductReservations} element={<ManageProductReservationsPage />} />
            <Route path={SITE_URLS.manageProductReservationDetail} element={<ManageProductReservationDetailPage />} />
            <Route path={SITE_URLS.myPoints} element={<MyAccountPointsHistoryPage />} />
            <Route path={SITE_URLS.myAddresses} element={<ManageAddressesPage />} />
            <Route path={SITE_URLS.myAddressesEdit} element={<ManageAddressesEditPage />} />
            <Route path={SITE_URLS.myAddressesCreate} element={<ManageAddressesCreatePage />} />
            {/* 404 page */}
            <Route path="*" element={<PageNotFound />} />
        </>
    );

    const isInMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true' || false;

    return (
        <>
            <Routes>
                {isInMaintenanceMode ? (
                    <Route path="*" element={<MaintenancePage />} />
                ) : (
                    <>
                        {urlSegments.length > 1 && supportedLanguages?.includes(urlSegments[1]) ? (
                            <Route path="/:language" element={<SiteSetup />}>
                                {availableRoutes()}
                            </Route>
                        ) : (
                            <Route path="/" element={<SiteSetup />}>
                                {availableRoutes()}
                            </Route>
                        )}
                    </>
                )}
            </Routes>
        </>
    );
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
    <BrowserRouter>
        <React.StrictMode>
            <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID ?? ''}>
                <ThemesProvider>
                    <DataProvider>
                        <NotificationsProvider>
                            <ServicesCartProvider>
                                <App />
                            </ServicesCartProvider>
                        </NotificationsProvider>
                    </DataProvider>
                </ThemesProvider>
            </GoogleOAuthProvider>
        </React.StrictMode>
    </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
